import { useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Camera, AlertTriangle } from "lucide-react";
import { CompanyKyc, ForeignKyc, ProxyKyc, IdentityFields, DealKyc, YesNo, Q, Sec, Opts } from "@/components/PortalKycExtra";
import { makeForm, portalChunks } from "@/lib/upload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full rounded-xl border-2 border-slate-200 px-3.5 py-3 text-[16px] outline-none focus:border-slate-400";
const btn = "w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-semibold bg-ec-green disabled:opacity-50";

// C · Qualidade de pessoa interveniente (quatro perguntas).
const PEP_PERSON = [
  ["self", "É pessoa politicamente exposta?", "Tem, ou teve nos últimos 12 meses, um cargo político ou público importante (deputado, autarca, juiz, embaixador, administrador de empresa pública…)."],
  ["family", "É membro próximo da família de uma pessoa politicamente exposta?", "Cônjuge, unido de facto, pais, filhos e os seus cônjuges."],
  ["business", "É pessoa estreitamente associada a uma pessoa politicamente exposta?", "Ex.: sócio na mesma empresa ou relação de negócios próxima."],
  ["other", "É titular de outro cargo político ou público?", "Ex.: membro de órgão de autarquia, de entidade reguladora ou de empresa municipal."],
];
const PEP_COMPANY = PEP_PERSON.map(([k, l, h]) => [k, `Algum gerente, sócio ou beneficiário efetivo: ${l.charAt(0).toLowerCase()}${l.slice(1)}`, h]);
const NAMED = { family: "parentesco", business: "relação" };

function PepQuestion({ k, label, hint, v, set, prefix = "kyc-pep" }) {
  const rel = NAMED[k];
  const t = (key, ph) => <input className={inp} value={v[key] || ""} onChange={(e) => set({ ...v, [key]: e.target.value })} placeholder={ph} data-testid={`${prefix}-${k}-${key}`} />;
  return (
    <Q label={label} hint={hint}>
      <YesNo value={v.answer} onChange={(a) => set({ ...v, answer: a })} testid={`${prefix}-${k}`} />
      {v.answer === true && (
        <div className="space-y-2 rounded-xl bg-slate-50 p-3" data-testid={`${prefix}-${k}-details`}>
          {rel ? <>{t("name", "Nome da pessoa politicamente exposta")}{t("relation", rel === "parentesco" ? "Parentesco (ex.: pai, cônjuge)" : "Relação (ex.: sócio)")}</>
            : <>{t("role", "Cargo")}<div className="grid grid-cols-2 gap-2">{t("since", "Desde (ano)")}{t("until", "Até (ou atual)")}</div></>}
        </div>
      )}
    </Q>
  );
}

function JobProof({ token, session, kyc, onChange }) {
  const [busy, setBusy] = useState(false);
  const send = async (file, later) => {
    setBusy(true);
    try {
      const fd = await makeForm([...(file ? [["file", file]] : []), ...(later ? [["later", "1"]] : [])], portalChunks(token, session));
      await axios.post(`${API}/portal/${token}/kyc-proof`, fd, { headers: { "X-Portal-Session": session } }); onChange();
    }
    finally { setBusy(false); }
  };
  if (kyc.job_proof_received) return <p className="text-sm text-slate-600" data-testid="kyc-proof-received">Comprovativo de profissão: recebido · em verificação</p>;
  return (
    <Q label="Comprovativo da profissão" hint="Ex.: recibo de vencimento, declaração da entidade patronal ou do início de atividade.">
      <label className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg text-white font-semibold text-sm bg-ec-green cursor-pointer" data-testid="kyc-proof-upload">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} Fotografar ou enviar PDF
        <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => e.target.files?.[0] && send(e.target.files[0])} />
      </label>
      {!kyc.job_proof_later
        ? <button type="button" onClick={() => send(null, true)} className="block mx-auto text-sm text-slate-500 underline py-1" data-testid="kyc-proof-later">Não tenho agora</button>
        : <p className="text-xs text-slate-500 text-center" data-testid="kyc-proof-pending">Fica para depois. Pode enviar quando tiver.</p>}
    </Q>
  );
}

const Check = ({ on, set, testid, children }) => (
  <label className="flex items-start gap-3 text-[15px] text-slate-800"><input type="checkbox" checked={!!on} onChange={(e) => set(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0 accent-[#12332B]" data-testid={testid} /><span>{children}</span></label>
);

// Passo «Identificação» (ficha da Lei 83/2017): só aparece se a agência ligar a recolha. Respostas só do cliente.
export function KycStep({ token, session, kyc, onDone, onReload, agency }) {
  const d = kyc.data || {};
  const tr = kyc.traits || {};
  const firm = kyc.company;
  const [resident, setResident] = useState(d.resident ?? null);
  const [a, setA] = useState(d.a || {});
  const [b, setB] = useState(d.b || {});
  const [deal, setDeal] = useState(d.d || {});
  const [pep, setPep] = useState(() => Object.fromEntries(PEP_PERSON.map(([k]) => [k, d.pep?.[k] || { answer: null }])));
  const [company, setCompany] = useState(d.company || {});
  const [foreign, setForeign] = useState(d.foreign || {});
  const [dec, setDec] = useState({ truth: false, share: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const showForeign = tr.foreign || resident === false;
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await axios.post(`${API}/portal/${token}/kyc`, {
        resident, pep, declaration: dec, ...(firm ? { company } : { a }), ...(tr.proxy ? { b } : {}), ...(kyc.seller ? {} : { d: deal }),
        ...(showForeign ? { foreign } : {}),
      }, { headers: { "X-Portal-Session": session } });
      onDone();
    } catch (ex) { const det = ex.response?.data?.detail; setErr(typeof det === "string" ? det : "Verifique os dados e tente de novo."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="kyc-step">
      <div>
        <h2 className="font-heading text-xl text-ec-green">Ficha de identificação</h2>
        <p className="text-sm text-slate-500 mt-1">A lei (Lei n.º 83/2017) obriga as imobiliárias a identificar quem vende ou compra.</p>
      </div>
      {kyc.correction && <div className="flex gap-2 text-sm bg-amber-50 text-amber-900 border border-amber-200 rounded-lg px-3 py-2" data-testid="kyc-correction"><AlertTriangle size={16} className="shrink-0 mt-0.5" /><span><b>Pedimos uma correção:</b> {kyc.correction}</span></div>}
      <Q label={firm ? "Pessoa coletiva" : "Pessoa singular"}>
        <Opts options={[["sim", "Residente em Portugal"], ["nao", "Não residente"]]} value={resident === true ? "sim" : resident === false ? "nao" : null} onChange={(x) => setResident(x === "sim")} testid="kyc-resident" />
      </Q>
      {!firm && <Sec title="A · Identificação completa" testid="kyc-a">
        <IdentityFields v={a} set={setA} prefix="kyc-a" />
        {kyc.job_proof && <JobProof token={token} session={session} kyc={kyc} onChange={onReload} />}
      </Sec>}
      {firm && <CompanyKyc v={company} set={setCompany} />}
      {tr.proxy && <ProxyKyc v={b} set={setB} />}
      <Sec title="C · Qualidade de pessoa interveniente" sub="Na maioria dos casos, a resposta é Não." testid="kyc-pep">
        {(firm ? PEP_COMPANY : PEP_PERSON).map(([k, label, hint]) => (
          <PepQuestion key={k} k={k} label={label} hint={hint} v={pep[k]} set={(v) => setPep((o) => ({ ...o, [k]: v }))} />
        ))}
      </Sec>
      {!kyc.seller && <DealKyc v={deal} set={setDeal} />}
      {showForeign && <ForeignKyc v={foreign} set={setForeign} seller={kyc.seller} />}
      <Sec title="Declaração" testid="kyc-declaration">
        <Check on={dec.truth} set={(v) => setDec((o) => ({ ...o, truth: v }))} testid="kyc-dec-truth">Declaro que as informações prestadas são verdadeiras e completas e que comunicarei qualquer alteração.</Check>
        <Check on={dec.share} set={(v) => setDec((o) => ({ ...o, share: v }))} testid="kyc-dec-share">Autorizo a partilha destes dados com a agência que representa a outra parte, apenas para cumprir a lei do branqueamento de capitais{agency ? ` (${agency})` : ""}.</Check>
      </Sec>
      {err && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2" data-testid="kyc-error">{err}</div>}
      <button type="submit" disabled={busy} className={btn} data-testid="kyc-submit">{busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} Enviar</button>
    </form>
  );
}

// Barra de passos do portal: Documentos está sempre disponível; os outros pela ordem.
export function StepBar({ steps, current, onPick }) {
  const firstOpen = steps.findIndex((s) => !s.done);
  return (
    <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }} data-testid="portal-steps">
      {steps.map((s, i) => {
        const can = s.key === "docs" || s.done || i <= firstOpen;
        const here = s.key === current;
        return (
          <button key={s.key} type="button" disabled={!can} onClick={() => onPick(s.key)} data-testid={`portal-step-${s.key}`} aria-current={here ? "step" : undefined}
            data-state={here ? "current" : s.done ? "done" : "todo"} className="text-left self-start disabled:cursor-not-allowed">
            <div className={`rounded-full transition-colors ${here ? "h-2 bg-ec-green" : s.done ? "h-1.5 mt-0.5 bg-ec-green/30" : "h-1.5 mt-0.5 bg-slate-200"}`} />
            <div className={`mt-1.5 text-[11px] leading-tight ${here ? "inline-block rounded-md bg-ec-green/10 px-1.5 py-0.5 -ml-1.5 text-ec-green font-bold" : s.done ? "text-slate-500" : "text-slate-400"}`}>{i + 1}. {s.label}{s.done && !here ? " ✓" : ""}</div>
          </button>
        );
      })}
    </div>
  );
}
